import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../../lib/api";
import { toast } from "sonner";

export function MoneyFlowAlertsTile() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState({});
  const [reconciling, setReconciling] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/money-flow-alerts")
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const recover = async (sid) => {
    setBusy((b) => ({ ...b, [sid]: "recovering" }));
    try {
      const r = await api.post("/admin/lost-cards/recover", { session_id: sid });
      toast.success(`Recovered: ${r.data?.kind || "card"} · $${r.data?.amount ?? "?"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recovery failed");
    } finally {
      setBusy((b) => { const c = { ...b }; delete c[sid]; return c; });
    }
  };

  const resendSms = async (cardId) => {
    setBusy((b) => ({ ...b, [cardId]: "resending" }));
    try {
      const r = await api.post("/admin/cards/resend-activation-sms", { card_id: cardId });
      const sms = r.data?.delivery?.sms;
      if (sms?.ok) toast.success(`SMS sent · sid=${(sms.sid || "").slice(0, 12)}…`);
      else toast.error(`SMS failed: ${sms?.error || "no carrier id returned"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resend failed");
    } finally {
      setBusy((b) => { const c = { ...b }; delete c[cardId]; return c; });
    }
  };

  const reconcileNow = async () => {
    setReconciling(true);
    try {
      const r = await api.post("/admin/reconcile-stuck-cards");
      const s = r.data?.summary || {};
      toast.success(`Reconciled: ${s.finalized} finalized · ${s.credited_topups} topups credited · ${s.skipped} skipped`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reconcile failed");
    } finally {
      setReconciling(false);
    }
  };

  if (!data) return null;
  const total = data.total_alerts || 0;
  const lost = data.lost_paid_sessions || {};
  const sms = data.active_without_activation_sms || {};
  const stuck = data.stuck_pending_payment || {};
  const isAlert = total > 0;

  return (
    <div
      className={`rounded-xl border p-5 ${isAlert ? "border-rose-300 bg-rose-50/40 ring-1 ring-rose-200" : "border-[#E5E0D8] bg-white"}`}
      data-testid="money-flow-alerts-tile"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={`inline-block w-2.5 h-2.5 rounded-full ${isAlert ? "bg-rose-500 animate-pulse" : "bg-emerald-500"}`}
              aria-hidden="true"
            />
            <p className="text-sm font-medium text-[#112318]">Money-flow alerts</p>
            {isAlert ? (
              <span
                className="text-[9px] tracking-[0.2em] uppercase px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 border border-rose-200"
                data-testid="money-flow-alerts-count"
              >
                {total} open
              </span>
            ) : (
              <span className="text-[9px] tracking-[0.2em] uppercase px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">
                all clear
              </span>
            )}
          </div>
          <p className="text-xs text-[#4A5C50] mt-1">
            Surfaces paid Stripe sessions with no DB record · activated cards whose receiver-SMS never sent · cards stuck in pending_payment &gt;15 min.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={reconcileNow}
            disabled={reconciling}
            className="text-[11px] tracking-[0.18em] uppercase px-3 py-1.5 rounded-md bg-[#184A30] text-white hover:bg-[#0F3320] disabled:opacity-50"
            data-testid="money-flow-reconcile-now"
          >
            {reconciling ? "Reconciling…" : "Reconcile now"}
          </button>
          <button
            onClick={load}
            className="text-[11px] tracking-[0.18em] uppercase px-3 py-1.5 rounded-md border border-[#184A30]/30 text-[#184A30] hover:bg-[#184A30]/5"
            data-testid="money-flow-refresh"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mt-5">
        <MoneyFlowKpi label="Lost paid sessions" count={lost.count || 0}
                     subtitle="Paid on Stripe · no DB record" tone={lost.count ? "danger" : "ok"} />
        <MoneyFlowKpi label="No activation SMS" count={sms.count || 0}
                     subtitle="Active card · receiver never texted" tone={sms.count ? "warn" : "ok"} />
        <MoneyFlowKpi label="Stuck pending" count={stuck.count || 0}
                     subtitle="Pending_payment · >15 min old" tone={stuck.count ? "warn" : "ok"} />
      </div>

      {!!(lost.samples || []).length && (
        <div className="mt-5 rounded-lg border border-rose-200 bg-white p-3" data-testid="money-flow-lost-list">
          <p className="text-[11px] tracking-[0.2em] uppercase text-rose-700 mb-2">Lost paid sessions</p>
          <div className="space-y-1">
            {lost.samples.map((s) => (
              <div key={s.session_id} className="flex flex-wrap items-center gap-2 text-xs py-1.5 border-t border-rose-100 first:border-t-0">
                <span className="mono text-[10px] text-[#4A5C50]">{s.session_id.slice(0, 24)}…</span>
                <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[10px]">{s.kind}</span>
                <span className="font-medium">${s.amount_usd?.toFixed(2)}</span>
                <span className="text-[#4A5C50] truncate max-w-[200px]">{s.email || "—"}</span>
                <button
                  onClick={() => recover(s.session_id)}
                  disabled={!!busy[s.session_id]}
                  className="ml-auto text-[10px] tracking-[0.16em] uppercase px-2 py-1 rounded bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50"
                  data-testid={`recover-session-${s.session_id.slice(0,12)}`}
                >
                  {busy[s.session_id] ? "…" : "Recover"}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {!!(sms.samples || []).length && (
        <div className="mt-3 rounded-lg border border-amber-200 bg-white p-3" data-testid="money-flow-sms-list">
          <p className="text-[11px] tracking-[0.2em] uppercase text-amber-700 mb-2">Active cards · receiver never SMS'd</p>
          <div className="space-y-1">
            {sms.samples.map((c) => (
              <div key={c.card_id} className="flex flex-wrap items-center gap-2 text-xs py-1.5 border-t border-amber-100 first:border-t-0">
                <span className="mono text-[10px] text-[#4A5C50]">…{c.card_id.slice(-8)}</span>
                {c.last4 && <span className="font-medium">·{c.last4}</span>}
                <span className="text-[#4A5C50]">{c.receiver_name || "—"}</span>
                <span className="mono text-[10px] text-[#4A5C50]">{c.receiver_phone || "—"}</span>
                <span className="font-medium">${c.balance_usd?.toFixed(2)}</span>
                {c.owner_name && (
                  <span className="text-[10px] text-slate-500" title={c.owner_email || ""}>· issued by {c.owner_name}</span>
                )}
                <button
                  onClick={() => resendSms(c.card_id)}
                  disabled={!!busy[c.card_id]}
                  className="ml-auto text-[10px] tracking-[0.16em] uppercase px-2 py-1 rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
                  data-testid={`resend-sms-${c.card_id.slice(0,12)}`}
                >
                  {busy[c.card_id] ? "…" : "Resend SMS"}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {!!(data.affected_customers || []).length && (
        <div className="mt-3 rounded-lg border border-[#E5E0D8] bg-white p-3" data-testid="money-flow-impact-list">
          <p className="text-[11px] tracking-[0.2em] uppercase text-[#184A30] mb-2">Customers affected · top {Math.min(5, data.affected_customers.length)}</p>
          <div className="space-y-1">
            {data.affected_customers.slice(0, 5).map((c, i) => (
              <div key={(c.owner_email || c.owner_name || i) + i} className="flex flex-wrap items-baseline gap-2 text-xs py-1.5 border-t border-[#E5E0D8] first:border-t-0">
                <span className="font-medium text-[#112318]">{c.owner_name || c.owner_email || "Unknown"}</span>
                {c.owner_email && c.owner_name && (
                  <span className="text-[10px] text-[#4A5C50]">{c.owner_email}</span>
                )}
                <span className="ml-auto mono text-[11px] text-[#184A30]">${(c.total_usd || 0).toFixed(2)}</span>
                <span className="text-[10px] text-[#4A5C50]">{c.alerts} alert{c.alerts === 1 ? "" : "s"}</span>
                <span className="text-[10px] text-slate-500 w-full sm:w-auto">{(c.kinds || []).join(" · ")}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MoneyFlowKpi({ label, count, subtitle, tone }) {
  const toneCls =
    tone === "danger" ? "border-rose-200 bg-rose-50 text-rose-800"
    : tone === "warn" ? "border-amber-200 bg-amber-50 text-amber-800"
    : "border-emerald-200 bg-emerald-50 text-emerald-800";
  return (
    <div className={`rounded-lg border ${toneCls} p-3`} data-testid={`money-flow-kpi-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <p className="text-[10px] tracking-[0.2em] uppercase opacity-70">{label}</p>
      <p className="text-2xl font-semibold mt-0.5">{count}</p>
      <p className="text-[11px] mt-1 opacity-70">{subtitle}</p>
    </div>
  );
}
